// Web Push Notifications Utility using Firebase Cloud Messaging
// Only works on ChromeOS devices

import { initializeApp, getApps, getApp, FirebaseApp } from "firebase/app";
import {
  getMessaging,
  getToken,
  onMessage,
  isSupported,
  Messaging,
  MessagePayload,
} from "firebase/messaging";
import { getPlatformInfo } from "./platformDetection";

export interface FCMTokenData {
  token: string;
  vapidKey: string;
}

// Firebase Configuration interface
export interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
}

// Use existing ChromeOS detection from platformDetection.ts
export { isChromeOS } from "./platformDetection";

// Use existing PWA detection from platformDetection.ts
export { isPWAMode as isPWADisplayMode } from "./platformDetection";

// Enable push notifications for ChromeOS only
const ENABLE_ALL_WEB_PLATFORMS = false;

export const isChromeOSWebBrowser = (): boolean => {
  // Use existing platform detection utilities
  const platformInfo = getPlatformInfo();

  // Only allow PWA and FCM on ChromeOS web browsers (not Electron or mobile apps)
  return platformInfo.isChromeOS && platformInfo.isWeb;
};

export class WebPushNotificationManager {
  private static instance: WebPushNotificationManager;
  private messaging: Messaging | null = null;
  private firebaseConfig: FirebaseConfig | null = null;
  private vapidKey: string = "";

  private constructor() {}

  public static getInstance(): WebPushNotificationManager {
    if (!WebPushNotificationManager.instance) {
      WebPushNotificationManager.instance = new WebPushNotificationManager();
    }
    return WebPushNotificationManager.instance;
  }

  private checkChromeOSSupport(): boolean {
    if (!isChromeOSWebBrowser()) {
      return false;
    }
    return true; // ChromeOS web browser - supported
  }

  public setFirebaseConfig(config: FirebaseConfig, vapidKey: string): void {
    if (!this.checkChromeOSSupport()) {
      return;
    }

    this.firebaseConfig = config;
    this.vapidKey = vapidKey;
  }

  public async initialize(): Promise<boolean> {
    if (!this.checkChromeOSSupport()) {
      return false;
    }

    try {
      const platform = ENABLE_ALL_WEB_PLATFORMS
        ? "[Desktop Web Browsers]"
        : "[ChromeOS]";

      // Check if Firebase messaging is supported
      const supported = await isSupported();
      if (!supported) {
        console.warn(
          `❌ ${platform} Firebase messaging is not supported in this browser`
        );
        console.warn(
          "💡 This browser does not support Firebase Cloud Messaging (FCM)"
        );
        return false;
      }

      // Check if service worker is supported
      if (!("serviceWorker" in navigator)) {
        console.warn("❌ [ChromeOS] Service workers are not supported");
        return false;
      }

      if (!this.firebaseConfig) {
        console.error("❌ [ChromeOS] Firebase config not set");
        return false;
      }

      // Initialize Firebase app if not already initialized
      let app: FirebaseApp;
      if (getApps().length === 0) {
        app = initializeApp(this.firebaseConfig);
      } else {
        app = getApp();
      }

      // Pass Firebase config to service worker
      await this.updateServiceWorkerConfig();

      // Use the main PWA service worker for Firebase messaging
      // This ensures notifications open in the PWA context, not browser
      let registration = await navigator.serviceWorker.getRegistration("/");

      if (!registration) {
        // Register the main service worker if not already registered
        registration = await navigator.serviceWorker.register("/sw.js", {
          scope: "/",
          updateViaCache: "none",
        });

        // Wait for the service worker to be ready
        registration = await navigator.serviceWorker.ready;
      }

      // Initialize Firebase messaging with the existing service worker
      // This tells Firebase to use /sw.js instead of /firebase-messaging-sw.js
      this.messaging = getMessaging(app);

      // Send Firebase config to the service worker
      // This associates Firebase messaging with the PWA context
      if (registration && registration.active) {
        registration.active.postMessage({
          type: "FIREBASE_CONFIG",
          config: this.firebaseConfig,
        });
      }

      // Wait for service worker to be ready (with timeout)
      try {
        await Promise.race([
          navigator.serviceWorker.ready,
          new Promise((_, reject) =>
            setTimeout(
              () => reject(new Error("Service worker ready timeout")),
              5000
            )
          ),
        ]);
      } catch {
        console.warn(
          "⚠️ [ChromeOS] Service worker ready timeout - continuing anyway"
        );
        // Continue anyway - the service worker might still work
      }

      // Setup service worker message listener immediately on initialization
      // This ensures notification clicks work even when app is in background
      this.setupServiceWorkerMessageListener();

      return true;
    } catch (error) {
      console.error(
        "❌ [ChromeOS] Failed to initialize Firebase messaging:",
        error
      );
      return false;
    }
  }

  public async requestPermission(): Promise<NotificationPermission> {
    if (!this.checkChromeOSSupport()) {
      return "denied";
    }

    if (!("Notification" in window)) {
      console.warn("❌ [ChromeOS] This browser does not support notifications");
      return "denied";
    }

    const permission = await Notification.requestPermission();

    return permission;
  }

  // NEW: User-initiated permission request with token generation
  public async enableNotifications(): Promise<string | null> {
    if (!this.checkChromeOSSupport()) {
      return null;
    }

    // Request permission (this MUST be called from user interaction)
    const permission = await this.requestPermission();
    if (permission !== "granted") {
      console.warn("❌ [ChromeOS] Push notification permission not granted");
      return null;
    }

    // Permission granted - now get FCM token

    const fcmToken = await this.subscribeToPushNotifications();

    if (fcmToken) {
      // Setup foreground message handler
      this.setupForegroundMessageHandler(() => {});
    }

    return fcmToken;
  }

  private async updateServiceWorkerConfig(): Promise<void> {
    if (!this.checkChromeOSSupport() || !this.firebaseConfig) {
      return;
    }

    try {
      // Send Firebase config to all service workers
      const registrations = await navigator.serviceWorker.getRegistrations();
      registrations.forEach((registration) => {
        if (registration.active) {
          registration.active.postMessage({
            type: "FIREBASE_CONFIG",
            config: this.firebaseConfig,
          });
        }
      });

      // Also set global variables for new service worker instances
      if ("serviceWorker" in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: "FIREBASE_CONFIG",
          config: this.firebaseConfig,
        });
      }
    } catch (error) {
      console.error(
        "❌ [ChromeOS] Failed to update service worker config:",
        error
      );
    }
  }

  public async getFCMToken(): Promise<string | null> {
    if (!this.checkChromeOSSupport()) {
      return null;
    }

    if (!this.messaging) {
      const platform = ENABLE_ALL_WEB_PLATFORMS
        ? "[All Platforms - Web]"
        : "[ChromeOS]";
      console.error(`❌ ${platform} Firebase messaging not initialized`);
      return null;
    }

    try {
      const platform = ENABLE_ALL_WEB_PLATFORMS
        ? "[All Platforms - Web]"
        : "[ChromeOS]";

      // Get the existing service worker registration
      const registration = await navigator.serviceWorker.getRegistration("/");

      if (!registration) {
        console.error(`❌ ${platform} Service worker not registered`);
        return null;
      }

      const token = await getToken(this.messaging, {
        vapidKey: this.vapidKey,
        serviceWorkerRegistration: registration, // Use our sw.js, not firebase-messaging-sw.js
      });

      if (token) {
        return token;
      } else {
        return null;
      }
    } catch (error) {
      const platform = ENABLE_ALL_WEB_PLATFORMS
        ? "[All Platforms - Web]"
        : "[ChromeOS]";
      console.error(`❌ ${platform} Failed to get FCM token:`, error);
      return null;
    }
  }

  public async subscribeToPushNotifications(): Promise<string | null> {
    if (!this.checkChromeOSSupport()) {
      return null;
    }

    const permission = await this.requestPermission();
    if (permission !== "granted") {
      console.warn("❌ [ChromeOS] Push notification permission not granted");
      return null;
    }

    return await this.getFCMToken();
  }

  public async getExistingSubscription(): Promise<string | null> {
    if (!this.checkChromeOSSupport()) {
      return null;
    }

    if (!this.messaging) {
      return null;
    }

    try {
      // Get the existing service worker registration
      const registration = await navigator.serviceWorker.getRegistration("/");

      if (!registration) {
        console.error("❌ [ChromeOS] Service worker not registered");
        return null;
      }

      // Try to get existing token
      const token = await getToken(this.messaging, {
        vapidKey: this.vapidKey,
        serviceWorkerRegistration: registration, // Use our sw.js
      });

      return token || null;
    } catch (error) {
      console.error("❌ [ChromeOS] Failed to get existing FCM token:", error);
      return null;
    }
  }

  public async unsubscribeFromPushNotifications(): Promise<boolean> {
    if (!this.checkChromeOSSupport()) {
      return false;
    }

    if (!this.messaging) {
      return false;
    }

    try {
      // FCM tokens are automatically managed by Firebase
      // When the app is uninstalled or permissions revoked, tokens become invalid

      return true;
    } catch (error) {
      console.error(
        "❌ [ChromeOS] Failed to unsubscribe from push notifications:",
        error
      );
      return false;
    }
  }

  public isPushSupported(): boolean {
    return (
      this.checkChromeOSSupport() &&
      "serviceWorker" in navigator &&
      "Notification" in window
    );
  }

  public async isFirebaseSupported(): Promise<boolean> {
    if (!this.checkChromeOSSupport()) {
      return false;
    }

    try {
      return await isSupported();
    } catch {
      return false;
    }
  }

  public setupForegroundMessageHandler(
    callback: (payload: MessagePayload) => void
  ): void {
    if (!this.checkChromeOSSupport()) {
      return;
    }

    if (!this.messaging) {
      console.error("❌ [ChromeOS] Firebase messaging not initialized");
      return;
    }

    onMessage(this.messaging, (payload: MessagePayload) => {
      // Check for auto-redirect: ALERT_TYPE = 1 in foreground
      if (payload.data?.ALERT_TYPE === "1" && payload.data?.ALERT_URL) {
        // Play sound for alerts
        if (payload.data?.sound) {
          if (window.chimeAudio) {
            window.chimeAudio.pause();
            window.chimeAudio.currentTime = 0;
          }
          window.chimeAudio = new Audio(
            `./assets/audio/${payload.data.sound}.wav`
          );
          window.chimeAudio.loop = true;
          window.chimeAudio.play().catch((error) => {
            console.error("❌ [ChromeOS] Failed to play alert sound:", error);
          });
        }

        // Auto-redirect immediately to ALERT_URL (no notification popup)
        window.location.href = payload.data.ALERT_URL;
        return; // Skip showing notification popup
      }

      // Also call the callback if provided
      callback(payload);
    });
  }

  private setupServiceWorkerMessageListener(): void {
    if (!this.checkChromeOSSupport()) {
      return;
    }

    // Listen for messages from service worker
    navigator.serviceWorker.addEventListener("message", (event) => {
      // Handle navigation messages from background notification clicks
      if (event.data.type === "NAVIGATE_TO_OPEN_URL" && event.data.url) {
        // Play sound for background notification clicks
        if (event.data.sound) {
          if (window.chimeAudio) {
            window.chimeAudio.pause();
            window.chimeAudio.currentTime = 0;
          }
          window.chimeAudio = new Audio(
            `./assets/audio/${event.data.sound}.wav`
          );
          window.chimeAudio.play().catch((error) => {
            console.error(
              "❌ [ChromeOS] Failed to play background notification sound:",
              error
            );
          });
        }

        window.location.href = event.data.url;
      }
    });
  }

  private async displayForegroundNotification(
    payload: MessagePayload
  ): Promise<void> {
    if (!this.checkChromeOSSupport()) {
      return;
    }

    // Check if we have notification permission
    if (Notification.permission !== "granted") {
      console.warn(
        "⚠️ [ChromeOS] Cannot show foreground notification - permission not granted"
      );
      return;
    }

    try {
      // Extract notification details
      const title =
        payload.notification?.title ||
        payload.data?.title ||
        "New Notification";
      const body =
        payload.notification?.body ||
        payload.data?.body ||
        "You have a new notification";

      // Create notification options
      const options: NotificationOptions = {
        body,
        data: payload.data,
        requireInteraction: true,
        silent: false,
        tag: payload.data?.sound
          ? `circadia-${payload.data.sound}`
          : "circadia-notification",
      };

      // Display the notification
      const notification = new Notification(title, options);

      // Handle notification click
      notification.onclick = (event) => {
        event.preventDefault();
        notification.close();

        if (window.chimeAudio) {
          window.chimeAudio.pause();
          window.chimeAudio.currentTime = 0;
        }

        // All foreground notification clicks navigate to OPEN_URL
        if (payload.data?.OPEN_URL) {
          window.location.href = payload.data.OPEN_URL;
        } else {
          if (window.parent) {
            window.parent.focus();
          } else {
            window.focus();
          }
          window.location.href = "/";
        }
      };

      // Auto-close after 10 seconds if not interacted with
      setTimeout(() => {
        notification.close();
      }, 10000);
    } catch (error) {
      console.error(
        "❌ [ChromeOS] Failed to display foreground notification:",
        error
      );
    }
  }
}

// Export singleton instance
export const webPushManager = WebPushNotificationManager.getInstance();

export const isWebPushSupported = (): boolean => {
  return webPushManager.isPushSupported();
};
